// Candlestick chart of the COMBINED netting backtest's trades — rendered at the bottom of the
// Discover backtest report so you can see where the assembled long+short strategy actually entered
// and exited on price (the StrategyReport's other charts are equity/returns, not price-with-trades).
//
// Distinct from CandidatePreviewChart (which charts ONE single-direction scan candidate): the
// netting result mixes long and short trades, so markers are coloured per-trade from the result's
// parallel `directions[]` array. Markers are placed by TIMESTAMP (entryTimes/exitTimes), matching
// the same 5000-bar window the netting backtest evaluated.

import { useEffect, useRef, useState } from "react";
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  createSeriesMarkers,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type UTCTimestamp,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import { Loader2 } from "lucide-react";
import { fetchLatestCandles } from "../../../api/candles";
import { toChartTime } from "../../../hooks/time";
import type { CandleDto, CandleInterval, MarketType } from "../../../api/types";

// Chart palette — inline (matches ChartPanel/CandidatePreviewChart; no shared theme module).
const BG = "#0d1117";
const TEXT = "#c9d1d9";
const GRID = "#161b22";
const BORDER = "#30363d";
const UP = "#3fb950";
const DOWN = "#f85149";

interface Props {
  coin: string;
  interval: CandleInterval;
  marketType: MarketType;
  /** Parallel per-trade arrays from the netting result (entryTimes[i]/exitTimes[i]/directions[i]/returns[i]). */
  entryTimes: string[];
  exitTimes: string[];
  directions: ("long" | "short")[];
  returns: number[];
  /** How many recent bars to display. Default 5000 — matches the backtest's evaluation window. */
  bars?: number;
}

// Cap markers so a several-hundred-trade backtest stays legible; per-trade text labels are dropped
// past LABEL_LIMIT (they otherwise stack into an unreadable wall). Mirrors CandidatePreviewChart.
const LABEL_LIMIT = 40;
const MARKER_TRADE_CAP = 120;

export function BacktestTradesChart({ coin, interval, marketType, entryTimes, exitTimes, directions, returns, bars = 5000 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof createChart> | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const [candles, setCandles] = useState<CandleDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tradeCount = Math.min(entryTimes.length, exitTimes.length, directions.length, returns.length);

  // Fetch candles for the display window whenever the asset tuple changes.
  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    setCandles(null);
    setError(null);
    fetchLatestCandles(coin, interval, marketType, bars, ac.signal)
      .then((c) => { if (!cancelled) setCandles(c); })
      .catch((e) => { if (!cancelled && (e as Error).name !== "AbortError") setError((e as Error).message); });
    return () => { cancelled = true; ac.abort(); };
  }, [coin, interval, marketType, bars]);

  // Build the chart once.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: BG }, textColor: TEXT },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { timeVisible: true, secondsVisible: false, borderColor: BORDER },
      rightPriceScale: { borderColor: BORDER },
      autoSize: true,
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: UP, downColor: DOWN,
      borderUpColor: UP, borderDownColor: DOWN,
      wickUpColor: UP, wickDownColor: DOWN,
    });
    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, []);
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      markersRef.current = null;
    };
  }, []);

  // Push candles + markers whenever data or the trades change.
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || !candles) return;

    const sorted = [...candles].sort((a, b) => toTs(a.openTime) - toTs(b.openTime));
    series.setData(sorted.map((c) => ({
      time: toTs(c.openTime) as UTCTimestamp,
      open: c.open, high: c.high, low: c.low, close: c.close,
    })));

    markersRef.current?.setMarkers(buildMarkers(entryTimes, exitTimes, directions, returns));
    chart.timeScale().fitContent();
  }, [candles, entryTimes, exitTimes, directions, returns]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-[360px] rounded-lg overflow-hidden border border-border">
        <div ref={containerRef} className="absolute inset-0" />
        {!candles && !error && (
          <div className="absolute inset-0 grid place-items-center bg-card/60">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 grid place-items-center text-xs text-destructive px-4 text-center">
            Couldn't load candles: {error}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1"><span className="text-emerald-400">▲</span> long entry</span>
        <span className="flex items-center gap-1"><span className="text-red-400">▼</span> short entry</span>
        <span className="flex items-center gap-1"><span className="text-amber-400">◆</span> exit (win)</span>
        <span className="flex items-center gap-1"><span style={{ color: "#a371f7" }}>◆</span> exit (loss)</span>
        <span>· {tradeCount} netting trades over the {bars}-bar window</span>
        {tradeCount > MARKER_TRADE_CAP && (
          <span className="text-amber-300/70">· showing the most recent {MARKER_TRADE_CAP}</span>
        )}
      </div>
    </div>
  );
}

// Build entry/exit markers from the netting result's parallel trade arrays, keyed by timestamp.
// Each trade's marker shape/position/colour is driven by its OWN direction (long vs short), so a
// netting run that mixes both reads correctly. Caps to the most-recent MARKER_TRADE_CAP trades.
function buildMarkers(
  entryTimes: string[],
  exitTimes: string[],
  directions: ("long" | "short")[],
  returns: number[],
): SeriesMarker<Time>[] {
  const markers: SeriesMarker<Time>[] = [];
  const total = Math.min(entryTimes.length, exitTimes.length, directions.length, returns.length);
  const start = Math.max(0, total - MARKER_TRADE_CAP); // most-recent N only
  const withText = total - start <= LABEL_LIMIT;
  for (let i = start; i < total; i++) {
    const isLong = directions[i] === "long";
    const win = returns[i]! >= 0;
    markers.push({
      time: toTs(entryTimes[i]!) as UTCTimestamp,
      position: isLong ? "belowBar" : "aboveBar",
      color: isLong ? UP : DOWN,
      shape: isLong ? "arrowUp" : "arrowDown",
      ...(withText ? { text: `#${i + 1} ${isLong ? "L" : "S"}` } : {}),
    });
    markers.push({
      time: toTs(exitTimes[i]!) as UTCTimestamp,
      position: isLong ? "aboveBar" : "belowBar",
      color: win ? "#d29922" : "#a371f7",
      shape: isLong ? "arrowDown" : "arrowUp",
      ...(withText ? { text: `${returns[i]! >= 0 ? "+" : ""}${returns[i]!.toFixed(1)}%` } : {}),
    });
  }
  // setMarkers requires ascending time order.
  return markers.sort((a, b) => (a.time as number) - (b.time as number));
}

function toTs(iso: string): number {
  return Math.floor(toChartTime(iso));
}
